/**
 * 인증 관련 API 서비스
 *
 * API 클라이언트 인스턴스를 인자로 받아 호출 (DI 패턴)
 * shared 레이어: config, workspace-pkg만 import 가능
 */

import type { ApiClient } from "@/shared/api/client";

export interface LoginRequest {
  email: string;
  memberPw: string;
}

export interface LoginResponse {
  token: string;
  id: string;
  businessName: string;
  saleAmt: number;
  userGroups: string;
}

/**
 * POST /users/kakao 응답 — client envelope 언래핑 후 두 가지 형태:
 * - 기존 사용자: { code:200, data:{…} } → 언래핑되어 { token, id, businessName } 도착
 * - 미등록 사용자: { code:400, message } → data 없어 언래핑 안 됨, 그대로 도착
 */
export type KakaoLoginResponse =
  { token: string; id: number; businessName: string } | { code: number; message: string };

export interface RegisterRequest {
  email: string;
  memberPw: string;
  businessName: string;
  registrationNo: string;
  businessAddress: string;
  businessDetailAddress: string;
  businessZipCd: string;
  mobileNo: string;
  memberGroups?: string;
  idKakao?: string;
  directBrandName?: string;
}

export interface AdminLoginResponse {
  token: string;
  name: string;
}

export interface UserInfo {
  id: string;
  email: string;
  businessName: string;
  userGroups: string;
  saleAmt: number;
  point: number;
}

/**
 * GET /brands/info 응답
 *
 * 레거시 백엔드는 Sequelize 인스턴스를 `{...brand, role}` 로 spread — `name` 등
 * 컬럼 필드와 `role` 이 top-level 에 섞여있다. `role === "SUPER_ADMIN"` 은 brand 592
 * (official.dangil33) 에 대해서만 세팅된다. 다른 브랜드는 role 자체가 없다.
 *
 * Sequelize 버전에 따라 `{ dataValues: { name, ... }, role }` 형태일 수도 있어
 * 두 경로를 모두 방어한다. `isSuperAdmin` 은 프론트에서 role 로 파생.
 */
export interface AdminInfo {
  id?: number;
  role?: string;
  userType?: string;
  name?: string;
  dataValues?: { id?: number; name?: string; [key: string]: unknown };
}

export interface SendSmsRequest {
  phoneNumber: string;
}

export interface BrandSendSmsRequest extends SendSmsRequest {
  email: string;
}

export interface VerifySmsRequest {
  code: string;
  phoneNumber: string;
}

export interface BrandVerifySmsRequest extends VerifySmsRequest {
  email: string;
}

export interface ResetPasswordRequest {
  phoneNumber: string;
  code: string;
  pwd: string;
}

export interface BrandResetPasswordRequest {
  email: string;
  phoneNumber: string;
  code: string;
  pwd: string;
}

export interface MyCertificationResponse {
  status: string;
  created_at: string;
  rejectReason?: string;
}

export interface CertifyOnlineDetailedRequest {
  siteUrl: string;
  smartstoreUrl: string;
  platformUrls: string;
  snsUrls: string;
  consents: boolean[];
}

/** ✅ verified against legacy backend 2026-04-19 — POST /certification/offline (returns CertificationRequest model instance) */
export interface CertifyOfflineResponse {
  id: number;
  user_id: number;
  type: "offline";
  status: "pending" | "approved" | "rejected";
  data: {
    shopType: string | null;
    shopTypeEtc: string | null;
    photoFrontUrl: string;
    photoDisplay1Url: string;
    photoDisplay2Url: string | null;
  };
  reject_reason: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
}

/** ✅ verified against legacy backend 2026-04-19 — POST /certification/online (returns CertificationRequest model instance) */
export interface CertifyOnlineDetailedResponse {
  id: number;
  user_id: number;
  type: "online";
  status: "pending" | "approved" | "rejected";
  data: {
    siteUrl: string;
    smartstoreUrl: string;
    platformUrls: string | null;
    snsUrls: string | null;
  };
  reject_reason: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface SendSmsResponse {
  message: string;
}

export interface VerifySmsResponse {
  verified: boolean;
}

/** ✅ verified against legacy backend 2026-04-19 — POST /users/password */
export interface ResetPasswordResponse {
  code: number;
  message: string;
}

export function createAuthService(client: ApiClient) {
  return {
    loginWithEmail: (data: LoginRequest) => client.post<LoginResponse>("/users/login", data),
    loginWithKakao: (idKakao: string) =>
      client.post<KakaoLoginResponse>("/users/kakao", { idKakao }),
    register: (data: RegisterRequest) =>
      client.post<{ user: { id: string } }>("/users/register", data),
    adminLogin: (data: LoginRequest) => client.post<AdminLoginResponse>("/brands/login", data),
    getCurrentUser: () => client.get<UserInfo>("/users"),
    deleteCurrentUser: () => client.del<{ id: number }>("/users/me"),
    getUserType: () => client.get<{ userType: string }>("/brands/user-type"),
    getAdminInfo: () => client.get<AdminInfo>("/brands/info"),
    certifyOffline: (data: FormData) =>
      client.post<CertifyOfflineResponse>("/certification/offline", data),
    sendSms: (data: SendSmsRequest) => client.post<SendSmsResponse>("/users/sms", data),
    verifySms: (data: VerifySmsRequest) =>
      client.post<VerifySmsResponse>("/users/sms/verify", data),
    resetPassword: (data: ResetPasswordRequest) =>
      client.post<ResetPasswordResponse>("/users/password", data),
    sendBrandSms: (data: BrandSendSmsRequest) => client.post<SendSmsResponse>("/brands/sms", data),
    verifyBrandSms: (data: BrandVerifySmsRequest) =>
      client.post<VerifySmsResponse>("/brands/sms/verify", data),
    resetBrandPassword: (data: BrandResetPasswordRequest) =>
      client.post<ResetPasswordResponse>("/brands/password", data),
    getMyCertification: () => client.get<MyCertificationResponse>("/certification/my"),
    certifyOnlineDetailed: (data: CertifyOnlineDetailedRequest) =>
      client.post<CertifyOnlineDetailedResponse>("/certification/online", data),
    uploadAttachment: (file: File, idUser: string) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("idUser", idUser);
      return client.post<{ url: string }>("/attachments/upload", formData);
    },
  };
}
